// Broadcast.js - Anlık Mesaj Gönderme Sayfası
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  FiSend,
  FiFile,
  FiImage,
  FiVideo,
  FiLink,
  FiTrash2,
} from "react-icons/fi";
import SharedLayout from "./SharedLayout";
import InfoModal from "./modals/InfoModals";
import apiClient from "./services/apiClient";
import ChannelSelector from "./components/ChannelSelector";
import "./App.css";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL; // Removed
const MAX_MEDIA_MB = 16;
const MAX_DOCUMENT_MB = 30;

const filenameFromUrl = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    const name = u.pathname.split("/").pop() || "";
    return decodeURIComponent(name);
  } catch {
    const parts = (url || "").split("/");
    return decodeURIComponent(parts[parts.length - 1] || url);
  }
};

const normalizeExternalUrl = (value) => {
  const raw = (value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const Broadcast = () => {
  const { userId } = useParams();

  // Form state
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [deliveryChannel, setDeliveryChannel] = useState("whatsapp");
  const [teamsEnabled] = useState(true); // System-level, no admin setup needed

  const [content, setContent] = useState({
    text: "",
    document: null, // { url, size, name }
    media: null, // { url, size, name }
    linkUrl: "",
  });

  // Groups state
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // Upload states
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [broadcastReport, setBroadcastReport] = useState(null);

  // Info modal state
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });

  const showInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      setLoadingGroups(true);
      // apiClient unwrap - use /groups.
      const data = await apiClient.get("/groups");
      const groupsList = Array.isArray(data) ? data : data?.groups || [];

      const formattedGroups = groupsList.map((group) => ({
        id: group.id,
        name: group.name,
        memberCount: group.members?.length || 0,
      }));
      setGroups(formattedGroups);
    } catch (err) {
      console.error("Error fetching groups:", err);
      showInfo(
        "error",
        "Grup listesi alınamadı",
        "Gruplar yüklenirken bir hata oluştu.",
      );
    } finally {
      setLoadingGroups(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Upload file helper
  const uploadFile = async (file, type) => {
    const allowedMediaTypes = [
      "video/mp4",
      "image/jpeg",
      "image/png",
      "image/webp",
    ];
    const allowedDocumentTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (type === "media" && !allowedMediaTypes.includes(file.type)) {
      showInfo(
        "warning",
        "Desteklenmeyen medya",
        "Sadece MP4, JPG, PNG veya WEBP dosyaları yükleyebilirsiniz.",
      );
      return null;
    }

    if (type === "document" && !allowedDocumentTypes.includes(file.type)) {
      showInfo(
        "warning",
        "Desteklenmeyen doküman",
        "Sadece PDF, DOC veya DOCX dosyaları yükleyebilirsiniz.",
      );
      return null;
    }

    const maxFileSize =
      type === "document"
        ? MAX_DOCUMENT_MB * 1024 * 1024
        : MAX_MEDIA_MB * 1024 * 1024;

    if (file.size > maxFileSize) {
      showInfo(
        "warning",
        "Dosya çok büyük",
        type === "document"
          ? `Doküman en fazla ${MAX_DOCUMENT_MB}MB olabilir.`
          : `Medya dosyası en fazla ${MAX_MEDIA_MB}MB olabilir.`,
      );
      return null;
    }

    const form = new FormData();
    form.append("file", file);

    try {
      const data = await apiClient.post(`/upload/${type}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return {
        url: data.file?.url || data.url,
        size: file.size,
        name: file.name,
        kind:
          type === "media" && file.type.startsWith("video/")
            ? "video"
            : type === "media"
              ? "image"
              : "document",
      };
    } catch (err) {
      console.error(`${type} upload error:`, err);
      const errorMessage = err.formatted?.message || `${type} yüklenemedi.`;
      showInfo("error", "Yükleme hatası", errorMessage);
      return null;
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploadingDocument(true);
    const uploaded = await uploadFile(file, "document");
    setUploadingDocument(false);

    if (uploaded) {
      setContent((prev) => ({ ...prev, document: uploaded }));
    }
  };

  // Handle media upload (image/video)
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploadingMedia(true);
    const uploaded = await uploadFile(file, "media");
    setUploadingMedia(false);

    if (uploaded) {
      setContent((prev) => ({ ...prev, media: uploaded }));
    }
  };

  // Remove document
  const removeDocument = async () => {
    if (content.document?.url) {
      const filename = content.document.url.split("/").pop();
      try {
        // apiClient unwrap
        await apiClient.delete(`/upload/${encodeURIComponent(filename)}`);
      } catch (err) {
        console.warn("Doküman silinemedi:", err.message);
      }
    }
    setContent((prev) => ({ ...prev, document: null }));
  };

  // Remove media
  const removeMedia = async () => {
    if (content.media?.url) {
      const filename = content.media.url.split("/").pop();
      try {
        // apiClient unwrap
        await apiClient.delete(`/upload/${encodeURIComponent(filename)}`);
      } catch (err) {
        console.warn("Medya silinemedi:", err.message);
      }
    }
    setContent((prev) => ({ ...prev, media: null }));
  };

  // Check if at least one content field is filled
  const hasContent = () => {
    return (
      content.text.trim() ||
      content.document ||
      content.media ||
      content.linkUrl.trim()
    );
  };

  const getContentSummary = () => ({
    hasText: Boolean(content.text.trim()),
    hasDocument: Boolean(content.document?.url),
    hasMedia: Boolean(content.media?.url),
    mediaKind: content.media?.kind || null,
    hasLink: Boolean(content.linkUrl.trim()),
  });

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedGroupId) {
      showInfo("warning", "Grup seçimi gerekli", "Lütfen bir grup seçiniz!");
      return;
    }

    if (!hasContent()) {
      showInfo(
        "warning",
        "İçerik gerekli",
        "Lütfen en az bir içerik türü ekleyiniz (metin, doküman, medya veya link)!",
      );
      return;
    }

    setIsSubmitting(true);

    // Build content object - only include non-empty fields
    const contentPayload = {};
    if (content.text.trim()) contentPayload.text = content.text.trim();
    if (content.document?.url)
      contentPayload.documentUrl = content.document.url;
    if (content.media?.url) {
      if (content.media.kind === "video") {
        contentPayload.videoUrl = content.media.url;
      } else {
        contentPayload.imageUrl = content.media.url;
      }
    }

    const normalizedLinkUrl = normalizeExternalUrl(content.linkUrl);
    if (normalizedLinkUrl) contentPayload.linkUrl = normalizedLinkUrl;

    const whatsappRequestBody = {
      adminId: parseInt(userId),
      targetType: "group",
      targetIds: parseInt(selectedGroupId),
      content: contentPayload,
      channel: "whatsapp",
    };

    const teamsRequestBody = {
      targetType: "group",
      targetIds: parseInt(selectedGroupId),
      content: contentPayload,
    };

    try {
      let response = null;

      if (deliveryChannel === "whatsapp") {
        response = await apiClient.post("/broadcast", whatsappRequestBody);
      } else if (deliveryChannel === "teams") {
        response = await apiClient.post("/teams/broadcast", teamsRequestBody);
      }

      const summary = getContentSummary();
      const selectedGroup = groups.find(
        (group) => String(group.id) === String(selectedGroupId),
      );

      const sentCount = response?.success
        ? response.sent || response.whatsapp?.sent || response.teams?.sent || 0
        : 0;

      if (!response || !response.success) {
        showInfo("error", "Hata", response?.error || "Bir hata oluştu.");
      } else {
        let channelText = deliveryChannel === "teams" ? "Teams" : "WhatsApp";

        const totalLearners =
          response.totalLearners || 0;

        setSuccessMessage(
          `✅ ${channelText} mesajı başarıyla ${sentCount} kişiye gönderildi!`,
        );

        setBroadcastReport({
          groupName: selectedGroup?.name || "-",
          recipientCount: sentCount,
          totalLearners,
          skippedCount: Math.max(0, totalLearners - sentCount),
          ...summary,
          whatsappSent: deliveryChannel === "whatsapp" ? sentCount : 0,
          teamsSent: deliveryChannel === "teams" ? sentCount : 0,
        });

        setContent({
          text: "",
          document: null,
          media: null,
          linkUrl: "",
        });
        setSelectedGroupId("");

        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error("Broadcast error:", error);
      const errorMessage =
        error.formatted?.message ||
        error.message ||
        "Mesaj gönderilirken bir hata oluştu.";
      showInfo("error", "Hata", errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedLayout pageTitle="ANLIK MESAJ GÖNDER">
      <InfoModal
        isOpen={info.open}
        onClose={closeInfo}
        type={info.type}
        title={info.title}
        message={info.message}
      />

      <div className="max-w-3xl mx-auto">
        {/* Success Banner */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-2xl mb-4 flex items-center justify-between shadow-sm">
            <span className="font-semibold text-lg">{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-700 hover:text-green-900 font-bold text-xl"
            >
              ×
            </button>
          </div>
        )}

        {broadcastReport && (
          <div className="bg-white border border-green-200 rounded-2xl p-5 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Gönderim Raporu
              </h3>
              <button
                type="button"
                onClick={() => setBroadcastReport(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Kapat
              </button>
            </div>

            <div className="grid md:grid-cols-4 gap-3 mb-4">
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Hedef Grup</div>
                <div className="font-semibold text-gray-800">
                  {broadcastReport.groupName}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Toplam Kişi</div>
                <div className="font-semibold text-gray-800">
                  {broadcastReport.totalLearners}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Gönderilen</div>
                <div className="font-semibold text-green-700">
                  {broadcastReport.recipientCount}
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <div className="text-xs text-gray-500">Atlanan</div>
                <div className="font-semibold text-orange-600">
                  {broadcastReport.skippedCount}
                </div>
              </div>
            </div>

            {/* Channel breakdown if using "both" */}
            {broadcastReport.whatsappSent > 0 && broadcastReport.teamsSent > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Kanal Detayı
                </div>
                <div className="flex gap-4">
                  <div>
                    <span className="text-xs text-gray-500">WhatsApp: </span>
                    <span className="font-semibold text-green-700">
                      {broadcastReport.whatsappSent}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Teams: </span>
                    <span className="font-semibold text-blue-700">
                      {broadcastReport.teamsSent}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">
                Gönderilen İçerik Türleri
              </div>
              <div className="flex flex-wrap gap-2">
                {broadcastReport.hasText && (
                  <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                    METİN
                  </span>
                )}
                {broadcastReport.hasDocument && (
                  <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-semibold">
                    DOKÜMAN
                  </span>
                )}
                {broadcastReport.hasMedia && (
                  <span className="px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-semibold">
                    {broadcastReport.mediaKind === "video" ? "VİDEO" : "GÖRSEL"}
                  </span>
                )}
                {broadcastReport.hasLink && (
                  <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                    LİNK
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 text-sm font-bold">
              0
            </span>
            Gönderim Kanalı
          </h2>

          <ChannelSelector
            value={deliveryChannel}
            onChange={setDeliveryChannel}
          />
        </div>

        {/* Grup Seçimi */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 text-sm font-bold">
              1
            </span>
            Hedef Grup Seçin
          </h2>

          {loadingGroups ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
              <span className="ml-3 text-gray-500">Gruplar yükleniyor...</span>
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Henüz hiç grubunuz yok. Önce bir grup oluşturun.
            </div>
          ) : (
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
            >
              <option value="">Grup seçiniz...</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} ({group.memberCount} üye)
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Mesaj İçeriği */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center mr-3 text-sm font-bold">
              2
            </span>
            Mesaj İçeriği
          </h2>

          {/* Metin */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiSend className="inline mr-2" />
              Metin Mesajı
            </label>
            <textarea
              value={content.text}
              onChange={(e) =>
                setContent((prev) => ({ ...prev, text: e.target.value }))
              }
              placeholder="Göndermek istediğiniz mesajı yazın..."
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all resize-none"
            />
          </div>

          {/* Medya Kaynakları */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              MEDYA KAYNAKLARI
            </label>
            <div className="flex flex-wrap items-center gap-3 text-sm">
              {/* Document Upload */}
              <label
                className={`px-4 py-2 rounded-lg cursor-pointer flex items-center gap-2 ${
                  uploadingDocument
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                }`}
              >
                <FiFile />
                {uploadingDocument ? "Yükleniyor..." : "DOKÜMAN YÜKLE"}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  disabled={uploadingDocument}
                  onChange={handleDocumentUpload}
                />
              </label>

              {/* Media Upload */}
              <label
                className={`px-4 py-2 rounded-lg cursor-pointer flex items-center gap-2 ${
                  uploadingMedia
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : "bg-purple-50 text-purple-600 hover:bg-purple-100"
                }`}
              >
                <FiImage />
                {uploadingMedia ? "Yükleniyor..." : "MEDYA YÜKLE"}
                <input
                  type="file"
                  accept="video/mp4,.mp4,image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  disabled={uploadingMedia}
                  onChange={handleMediaUpload}
                />
              </label>
            </div>

            <div className="mt-3 text-xs text-gray-500 space-y-1">
              <div>• Doküman limiti: 30MB</div>
              <div>• Medya limiti: 16MB</div>
              <div>• Desteklenen medya: JPG, PNG, WEBP, MP4</div>
              <div>• Desteklenen doküman: PDF, DOC, DOCX</div>
            </div>

            {/* Uploaded Files List */}
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {/* Document */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="font-semibold text-sm mb-2 flex items-center">
                  <FiFile className="mr-2" /> Doküman
                </div>
                {content.document ? (
                  <div className="flex items-center justify-between text-sm">
                    <a
                      href={content.document.url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-blue-600 hover:underline flex-1"
                      title={content.document.url}
                    >
                      {content.document.name ||
                        filenameFromUrl(content.document.url)}
                    </a>
                    <button
                      type="button"
                      className="ml-2 text-red-500 hover:text-red-600"
                      onClick={removeDocument}
                      title="Kaldır"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Henüz doküman yok.</p>
                )}
              </div>

              {/* Media */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="font-semibold text-sm mb-2 flex items-center">
                  <FiImage className="mr-2" /> Medya (Görsel/Video)
                </div>
                {content.media ? (
                  <div className="flex items-center justify-between text-sm">
                    <a
                      href={content.media.url}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-blue-600 hover:underline flex-1"
                      title={content.media.url}
                    >
                      {content.media.name || filenameFromUrl(content.media.url)}
                    </a>
                    <button
                      type="button"
                      className="ml-2 text-red-500 hover:text-red-600"
                      onClick={removeMedia}
                      title="Kaldır"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Henüz medya yok.</p>
                )}
              </div>
            </div>
          </div>

          {/* Link URL */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiLink className="inline mr-2" />
              Harici Link (Opsiyonel)
            </label>
            <input
              type="url"
              value={content.linkUrl}
              onChange={(e) =>
                setContent((prev) => ({ ...prev, linkUrl: e.target.value }))
              }
              placeholder="https://example.com"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
            />
          </div>
        </div>

        {/* Gönder Butonu */}
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedGroupId || !hasContent()}
            className={`px-8 py-3 rounded-lg font-semibold text-white flex items-center transition-all ${
              isSubmitting || !selectedGroupId || !hasContent()
                ? "bg-gray-400 cursor-not-allowed"
                : "green-button hover:opacity-90"
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Gönderiliyor...
              </>
            ) : (
              <>
                <FiSend className="mr-2" />
                Mesajı Gönder
              </>
            )}
          </button>
        </div>
      </div>
    </SharedLayout>
  );
};

export default Broadcast;
